import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Users, Target as TargetIcon, Activity } from "lucide-react";
import api from "@/lib/api";
import { AdminLayout } from "@/components/Layout";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function AdminOrgDetail() {
  const { orgId } = useParams();
  const nav = useNavigate();
  const [org, setOrg] = useState(null);
  const [overview, setOverview] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [filters, setFilters] = useState({ location_id: "", activity_type_id: "", start: "", end: "" });

  const params = useMemo(() => ({
    organization_id: orgId,
    ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)),
  }), [orgId, filters]);

  useEffect(() => {
    api.get("/organizations").then(r => setOrg(r.data.find(o => o.id === orgId)));
    api.get("/locations").then(r => setLocations(r.data));
    api.get("/activity-types").then(r => setActivityTypes(r.data));
  }, [orgId]);

  useEffect(() => {
    api.get("/admin/stats/overview", { params }).then(r => setOverview(r.data));
    api.get("/admin/registrations", { params: { ...params, limit: 50 } }).then(r => setRegistrations(r.data));
  }, [params]);

  const applyPreset = (preset) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === "today") { const t = iso(today); setFilters(f => ({ ...f, start: t, end: t })); }
    else if (preset === "week") {
      const d = new Date(today); const day = (d.getDay() + 6) % 7;
      const monday = new Date(d); monday.setDate(d.getDate() - day);
      setFilters(f => ({ ...f, start: iso(monday), end: iso(today) }));
    } else if (preset === "month") {
      setFilters(f => ({ ...f, start: iso(new Date(today.getFullYear(), today.getMonth(), 1)), end: iso(today) }));
    } else if (preset === "quarter") {
      const q = Math.floor(today.getMonth() / 3);
      setFilters(f => ({ ...f, start: iso(new Date(today.getFullYear(), q * 3, 1)), end: iso(today) }));
    } else if (preset === "all") setFilters(f => ({ ...f, start: "", end: "" }));
  };

  if (!org) return <AdminLayout title="Laddar…"><div className="text-slate-400">Laddar…</div></AdminLayout>;

  return (
    <AdminLayout
      title={org.name}
      subtitle="Arbetarekommun · Socialdemokraterna Skåne"
      actions={
        <Button variant="outline" data-testid="org-detail-back" onClick={() => nav("/admin")} className="rounded-full">
          <ArrowLeft size={14} className="mr-1"/> Tillbaka till översikt
        </Button>
      }
    >
      <div className="flex flex-wrap gap-1 mb-4 bg-white border border-slate-200 rounded-full p-1 w-fit">
        {[
          { key: "today", label: "Idag" }, { key: "week", label: "Vecka" },
          { key: "month", label: "Månad" }, { key: "quarter", label: "Kvartal" },
          { key: "all", label: "Allt" },
        ].map(p => (
          <button key={p.key} data-testid={`orgd-quick-${p.key}`} onClick={() => applyPreset(p.key)}
            className="px-4 py-1.5 rounded-full text-sm font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors">
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div>
          <label className="text-xs text-slate-500">Plats</label>
          <Select value={filters.location_id || "all"} onValueChange={v => setFilters(f => ({ ...f, location_id: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="orgd-loc"><SelectValue placeholder="Alla"/></SelectTrigger>
            <SelectContent><SelectItem value="all">Alla</SelectItem>{locations.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-slate-500">Aktivitetstyp</label>
          <Select value={filters.activity_type_id || "all"} onValueChange={v => setFilters(f => ({ ...f, activity_type_id: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="orgd-at"><SelectValue placeholder="Alla"/></SelectTrigger>
            <SelectContent><SelectItem value="all">Alla</SelectItem>{activityTypes.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="text-xs text-slate-500">Från</label><Input data-testid="orgd-start" type="date" value={filters.start} onChange={e => setFilters(f => ({ ...f, start: e.target.value }))}/></div>
        <div><label className="text-xs text-slate-500">Till</label><Input data-testid="orgd-end" type="date" value={filters.end} onChange={e => setFilters(f => ({ ...f, end: e.target.value }))}/></div>
      </div>

      {!overview && <div className="text-slate-400">Laddar…</div>}
      {overview && (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card highlight label="Samtal totalt" value={overview.total_conversations} tid="orgd-total"/>
          <Card label="Idag" value={overview.today_conversations} tid="orgd-today"/>
          <Card label="Denna vecka" value={overview.week_conversations} tid="orgd-week"/>
          <Card label="Denna månad" value={overview.month_conversations} tid="orgd-month"/>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <SmallCard icon={<Activity size={14}/>} label="Aktiviteter" value={overview.total_activities}/>
          <SmallCard icon={<Users size={14}/>} label="Aktiva medlemmar" value={overview.active_members}/>
          <SmallCard icon={<Users size={14}/>} label="Registrerade medlemmar" value={overview.total_members}/>
          <SmallCard icon={<TargetIcon size={14}/>} label="Snitt / aktivitet" value={overview.avg_conversations_per_activity}/>
        </div>

        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Samtal över tid (senaste 30 dagarna)</div>
          {overview.timeline.length > 0 ? (
            <div className="h-64"><ResponsiveContainer>
              <LineChart data={overview.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)}/>
                <YAxis tick={{ fontSize: 11 }}/>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}/>
                <Line type="monotone" dataKey="conversations" stroke="#E1251B" strokeWidth={2.5} dot={{ r: 3 }}/>
              </LineChart>
            </ResponsiveContainer></div>
          ) : <div className="text-sm text-slate-400 py-10 text-center">Ingen data för valt intervall.</div>}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-extrabold text-slate-900">Senaste registreringar</h2>
            <Link to={`/admin/veckorapport?org=${orgId}`} className="text-sm font-semibold text-[#E1251B] hover:underline">Se veckorapport →</Link>
          </div>
          {registrations.length === 0 ? <div className="text-sm text-slate-400 py-6 text-center">Inga registreringar i valt intervall.</div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Datum</TableHead><TableHead>Aktivitetstyp</TableHead><TableHead>Plats</TableHead>
                <TableHead className="text-right">Samtal</TableHead>
              </TableRow></TableHeader>
              <TableBody>{registrations.map(r => (
                <TableRow key={r.id} data-testid={`orgd-reg-${r.id}`}>
                  <TableCell>{r.activity_date}</TableCell>
                  <TableCell>{r.activity_type_name}</TableCell>
                  <TableCell>{r.location_name}</TableCell>
                  <TableCell className="text-right font-heading font-bold number-tabular">{r.conversation_count}</TableCell>
                </TableRow>))}</TableBody>
            </Table>
          )}
        </div>
      </>)}
    </AdminLayout>
  );
}

function Card({ label, value, highlight, tid }) {
  return (
    <div className={`rounded-2xl p-6 ${highlight ? "bg-[#E1251B] text-white" : "bg-white border border-slate-200"}`} data-testid={tid}>
      <div className={`text-[11px] uppercase tracking-widest font-semibold ${highlight ? "text-white/70" : "text-slate-500"}`}>{label}</div>
      <div className="font-heading text-4xl font-extrabold mt-1 number-tabular">{(value || 0).toLocaleString("sv-SE")}</div>
    </div>
  );
}
function SmallCard({ icon, label, value }) {
  return (
    <div className="rounded-xl p-4 bg-white border border-slate-200">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">{icon}{label}</div>
      <div className="font-heading text-2xl font-extrabold text-slate-900 mt-1 number-tabular">{typeof value === "number" ? value.toLocaleString("sv-SE") : value}</div>
    </div>
  );
}
